import { PostBackgroundItemNo } from '../../constants/Post/postBackground';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import {
  AttachmentAudio,
  AttachmentImage,
  AttachmentVideo,
  TtsLocale,
  Uuid,
} from '../Base';
import { TextOrderingUnit } from '../GameStore';
import { RepositoryElement } from '../Repository';

export enum ChannelType {
  User = 'user',
  Space = 'classroom',
  Post = 'post',
}

export interface Channel {
  type: ChannelType;
  id: Uuid;
}

export enum PostState {
  Available = 'available',
}

export enum Audience {
  Public = 'public',
  Followers = 'followers',
  Friends = 'friends',
  Self = 'self',
  Members = 'members',
  Moderators = 'moderators',
  Admins = 'admins',
}

export enum Purpose {
  Happy = 'happy',
  Stressful = 'stressful',
  Sad = 'sad',
  Lonely = 'lonely',
  Proud = 'proud',
  Doubt = 'doubt',
  Angry = 'angry',
  Weak = 'weak',
  Anxious = 'anxious',
  Hopeless = 'hopeless',
  Frightened = 'frightened',
  Painful = 'painful',
  HowToUseJoyajoy = 'howToUseJoyajoy',
}

interface TabCategories {
  tab: string;
  categories: string[];
  pinPosition?: string | null; // '?' makes it optional, and it can be string or null
}

interface Statistics {
  shareCount: number | null;
  cheerCount: number | null;
  likeCount: number | null;
  replyCount: number | null;
  viewCount: number | null;
}

interface GiftAcquisitions {
  [key: string]: number; // A mapping of string keys to number values
}

enum AlbumType {
  album = 'gameSet',
}

interface PostAlbum {
  type: AlbumType;
  id: Uuid;
}

interface Graphics {
  image?: AttachmentImage;
  video?: AttachmentVideo;
}

// content
export enum PostContentType {
  Normal = 'normal',
  Space = 'classroom',
  Album = 'gameSet',
  Text = 'text',
  User = 'user',
  Post = 'post',
  TextReply = 'textReply',
  MediaReply = 'mediaReply',
  GiftsReply = 'giftsReply',
  MultiplayerGame = 'multiplayerGame',
  Game = 'game',
  PostGame = 'postGame',
  MultiplayerGameSession = 'multiplayerGameSession',
}

type NormalContent = {
  type: PostContentType.Normal;
  graphics?: Graphics[];
  audio?: AttachmentAudio;
  link?: {
    url: string;
    title?: string;
    description?: string;
    image?: AttachmentImage;
  };
  text?: string;
};
type TextContent = {
  type: PostContentType.Text;
  audio?: AttachmentAudio;
  background: PostBackgroundItemNo;
  text: string;
};
type PostContent = {
  type: PostContentType.Post;
  postId: Uuid;
  text?: string;
};
type GameSetContent = {
  type: PostContentType.Album;
  classroomId: Uuid;
  gameSetId: Uuid;
  text?: string;
};
type GameContent = {
  type: PostContentType.Game;
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  gameId: Uuid;
};
type UserContent = {
  type: PostContentType.User;
  userId: Uuid;
};
type SpaceContent = {
  type: PostContentType.Space;
  classroomId: Uuid;
};
type MultiplayerGameSessionContent = {
  classroomId: Uuid;
  sessionId: Uuid;
  type: PostContentType.MultiplayerGameSession;
  multiplayerGameId: Uuid;
};
type MultiplayerGameContent = {
  classroomId: Uuid;
  type: PostContentType.MultiplayerGame;
  multiplayerGameId: Uuid;
};
type PostGameContent = {
  type: PostContentType.PostGame;
  audio?: AttachmentAudio;
  graphics?: Graphics[];
  text?: string;
  textGame?: TextGame;
  imageGames: ImageGame[];
  weeklyLeaderboard: null;
  link?: {
    url: string;
    title?: string;
    description?: string;
    image?: AttachmentImage;
  };
  previews?: Graphics[];
};

interface Background {
  set: number;
}
interface ImageGame {
  timeLimit: string | number; //  either "auto" or an integer value
  gameData: {
    pieces: number;
    instruction: {
      text: string;
    };
  };
  maxScore: number;
  scenario: string;
  puzzle: number[];
  background: Background;
}

interface TextGame {
  timeLimit: string | number; //  either "auto" or an integer value
  background: Background;
  gameData: {
    answer: {
      words: Word[];
      locale: TtsLocale;
      soundMode: string;
      answerFirstLetter: boolean;
      isLyrics?: boolean;
      useColor?: boolean;
    };
    instruction: {
      text: string;
    };
  };
  maxScore: number;
  scenario: string;
}

interface Word {
  word: string;
  units: TextOrderingUnit[];
}

type Content =
  | NormalContent
  | TextContent
  | PostContent
  | GameSetContent
  | UserContent
  | SpaceContent
  | GameContent
  | PostGameContent
  | MultiplayerGameSessionContent
  | MultiplayerGameContent;

export interface PostJson<T = number> {
  readonly postId: Uuid;
  channel: Channel;
  createdBy: Uuid;
  createdAt: T;
  lastModifiedAt: T;
  lastEditedAt: T;
  state: PostState;
  purpose: Purpose;
  replyTo: string | null;
  audience: Audience | Uuid[];
  viewOnlyAudience?: Audience | null;
  tabCategories?: TabCategories | null;
  pinPosition?: string | null;
  isPostedAsChannel: boolean;
  isAnnouncement: boolean;
  disabledLike: boolean;
  disabledCheer: boolean;
  album: PostAlbum;
  content: Content;
  statistics: Statistics;
  giftAcquisitions: GiftAcquisitions;
}

export class Post extends RepositoryElement implements PostJson<Date> {
  readonly postId!: Uuid;
  channel!: Channel;
  createdBy!: Uuid;
  createdAt!: Date;
  lastModifiedAt!: Date;
  lastEditedAt!: Date;
  state!: PostState;
  purpose!: Purpose;
  replyTo!: string | null;
  audience!: Audience | Uuid[];
  viewOnlyAudience?: Audience | null;
  tabCategories?: TabCategories | null;
  pinPosition?: string | null;
  isPostedAsChannel!: boolean;
  isAnnouncement!: boolean;
  disabledLike!: boolean;
  disabledCheer!: boolean;
  album!: PostAlbum;
  content!: Content;
  statistics!: Statistics;
  giftAcquisitions!: GiftAcquisitions;

  constructor(post: PostJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(post));
  }

  id(): string {
    return this.postId;
  }

  toJSON(): PostJson {
    return deepTransformDate(this) as unknown as PostJson;
  }

  // Additional methods related to the Post class can be added here
}
